/**
 * Which priorities appear in "Everything open", remembered across sessions.
 *
 * Distinct from the filter bar on purpose. Those filters live in the URL
 * because they describe a view you are looking at right now and might want to
 * link to; this is a standing preference about what that section is *for* --
 * "I never want to see low priority up there" -- so it belongs in local
 * storage and survives closing the browser.
 *
 * It only narrows the master list. The category boards below always show
 * everything, because hiding items there would make a subcategory's own count
 * disagree with the rows under it.
 */

import { useCallback, useEffect, useState } from 'react'

import type { ItemPriority } from '@/api/types'
import { PRIORITIES } from '@/api/types'

const STORAGE_KEY = 'docket.masterListPriorities'

type Visibility = Record<ItemPriority, boolean>

const ALL_VISIBLE: Visibility = {
  imminent: true,
  high: true,
  medium: true,
  low: true,
}

function read(): Visibility {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return ALL_VISIBLE
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return ALL_VISIBLE
    const stored = parsed as Partial<Record<string, unknown>>
    // Rebuilt key by key rather than trusted wholesale: a value written by an
    // older version, or edited by hand, must not leave a priority undefined
    // and therefore permanently invisible.
    return PRIORITIES.reduce<Visibility>((acc, priority) => {
      acc[priority] = stored[priority] === false ? false : true
      return acc
    }, { ...ALL_VISIBLE })
  } catch {
    return ALL_VISIBLE
  }
}

export function usePriorityVisibility() {
  const [visible, setVisible] = useState<Visibility>(read)

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(visible))
    } catch {
      // Private browsing or a full quota. The preference simply is not
      // remembered; the app still works.
    }
  }, [visible])

  const toggle = useCallback((priority: ItemPriority) => {
    setVisible((current) => {
      const next = { ...current, [priority]: !current[priority] }
      // Turning the last one off would empty the section with no obvious way
      // back, so the final visible priority cannot be switched off.
      if (PRIORITIES.every((p) => !next[p])) return current
      return next
    })
  }, [])

  const showAll = useCallback(() => setVisible({ ...ALL_VISIBLE }), [])

  const hiddenCount = PRIORITIES.filter((p) => !visible[p]).length

  return { visible, toggle, showAll, hiddenCount }
}
